import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, User, Menu, X, Search, LogOut, ShieldCheck, ChevronDown, Package, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

// Mega-menu data — international standard category drilldowns
const MEGA = {
  MEN: {
    columns: [
      {
        title: "TOPS",
        links: [
          { label: "Hoodies & Sweatshirts", to: "/shop/men?cat=tops" },
          { label: "T-Shirts", to: "/shop/men?cat=tops" },
          { label: "Polos & Shirts", to: "/shop/men?cat=tops" },
          { label: "Jackets", to: "/shop/men?cat=tops" },
        ],
      },
      {
        title: "BOTTOMS",
        links: [
          { label: "Joggers", to: "/shop/men?cat=bottoms" },
          { label: "Cargo Pants", to: "/shop/men?cat=bottoms" },
          { label: "Jeans", to: "/shop/men?cat=bottoms" },
          { label: "Shorts", to: "/shop/men?cat=bottoms" },
        ],
      },
      {
        title: "FOOTWEAR & ACCESSORIES",
        links: [
          { label: "Sneakers", to: "/shop/men?cat=footwear" },
          { label: "Caps & Beanies", to: "/shop/men?cat=accessories" },
          { label: "Bags", to: "/shop/men?cat=accessories" },
          { label: "Socks", to: "/shop/men?cat=accessories" },
        ],
      },
    ],
    feature: {
      title: "DROP 014",
      subtitle: "STATIC × ELMNTS",
      cta: "SHOP DROP",
      to: "/drops",
      image: "https://images.pexels.com/photos/14241847/pexels-photo-14241847.jpeg",
    },
  },
  WOMEN: {
    columns: [
      {
        title: "TOPS",
        links: [
          { label: "Crop Tops", to: "/shop/women?cat=tops" },
          { label: "Oversized Tees", to: "/shop/women?cat=tops" },
          { label: "Hoodies", to: "/shop/women?cat=tops" },
          { label: "Tank Tops", to: "/shop/women?cat=tops" },
        ],
      },
      {
        title: "BOTTOMS",
        links: [
          { label: "Wide-Leg Pants", to: "/shop/women?cat=bottoms" },
          { label: "Joggers", to: "/shop/women?cat=bottoms" },
          { label: "Skirts", to: "/shop/women?cat=bottoms" },
          { label: "Shorts", to: "/shop/women?cat=bottoms" },
        ],
      },
      {
        title: "FOOTWEAR & ACCESSORIES",
        links: [
          { label: "Sneakers", to: "/shop/women?cat=footwear" },
          { label: "Sunglasses", to: "/shop/women?cat=accessories" },
          { label: "Jewelry", to: "/shop/women?cat=accessories" },
          { label: "Bags", to: "/shop/women?cat=accessories" },
        ],
      },
    ],
    feature: {
      title: "NEW IN",
      subtitle: "Curated Picks SS26",
      cta: "EXPLORE",
      to: "/shop/women",
      image: "https://images.unsplash.com/photo-1768084356884-22bb77e76931",
    },
  },
};

const NAV = [
  { to: "/shop/men", label: "MEN", mega: "MEN" },
  { to: "/shop/women", label: "WOMEN", mega: "WOMEN" },
  { to: "/drops", label: "DROPS" },
  { to: "/shop/collabs", label: "COLLABS" },
  { to: "/shop/sale", label: "SALE" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const [q, setQ] = useState("");
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  // Close user dropdown on outside click or Escape
  useEffect(() => {
    if (!userOpen) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setUserOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [userOpen]);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop/all?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  const handleLogout = async () => {
    setUserOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <>
      {/* Backdrop dim when any dropdown is open — international ecommerce standard */}
      {(userOpen || activeMega) && (
        <div
          className="fixed inset-0 top-[88px] bg-black/30 z-30 backdrop-blur-[2px] animate-fade-up pointer-events-none lg:pointer-events-auto"
          onClick={() => setActiveMega(null)}
          aria-hidden="true"
          data-testid="nav-backdrop"
        />
      )}

    <header className="sticky top-0 z-50 bg-white border-b border-black/10">
      {/* Top marquee */}
      <div className="overflow-hidden bg-black text-white text-[10px] font-display tracking-[0.25em]">
        <div className="ticker whitespace-nowrap py-1.5 flex">
          {[0, 1].map((n) => (
            <div key={`marquee-loop-${n}`} className="flex shrink-0">
              {["FREE SHIPPING OVER ₹1499", "DROP 014 LIVE NOW", "USE CODE FIRST10 — 10% OFF", "STATIC × ELMNTS — LIMITED"].map((t) => (
                <span key={`${n}-${t}`} className="px-10">◆ {t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Mobile menu */}
          <button
            className="lg:hidden text-black"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>

          <Link to="/" className="flex items-center gap-2.5 text-black group" data-testid="logo-link" aria-label="BYONDELMNTS home">
            <img src="/logo-be-512.png" alt="" className="h-8 w-8 object-contain shrink-0" />
            <span className="font-display text-2xl tracking-tighter leading-none">BYONDELMNTS</span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-8 h-full"
            onMouseLeave={() => setActiveMega(null)}
          >
            {NAV.map((n) => (
              <div
                key={n.to}
                onMouseEnter={() => setActiveMega(n.mega || null)}
                className="h-full flex items-center"
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-sm tracking-widest transition-colors flex items-center gap-1 ${
                      isActive || activeMega === n.mega ? "text-[#FF3B30]" : "text-black hover:text-[#FF3B30]"
                    }`
                  }
                >
                  {n.label}
                  {n.mega && <ChevronDown size={12} className={`transition-transform ${activeMega === n.mega ? "rotate-180" : ""}`} />}
                </NavLink>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-4 text-black">
            <button
              className="hover:text-[#FF3B30] transition-colors"
              onClick={() => setSearchOpen((s) => !s)}
              data-testid="search-btn"
              aria-label="Search"
            >
              <Search size={20} />
            </button>

            <Link to="/wishlist" className="relative hover:text-[#FF3B30] transition-colors" data-testid="wishlist-link">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF3B30] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <Link to="/cart" className="relative hover:text-[#FF3B30] transition-colors" data-testid="cart-link">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF3B30] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  className="hover:text-[#FF3B30] transition-colors flex items-center"
                  data-testid="user-menu-btn"
                  onClick={() => setUserOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={userOpen}
                >
                  <User size={20} />
                </button>
                {userOpen && (
                  <div
                    role="menu"
                    style={{ backgroundColor: "#ffffff" }}
                    className="absolute right-0 top-full mt-3 z-[60] w-72 border border-black/20 shadow-[0_8px_32px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)] animate-fade-up"
                    data-testid="user-menu-panel"
                  >
                    {/* Caret pointer */}
                    <span className="absolute -top-1.5 right-3 w-3 h-3 bg-white border-t border-l border-black/10 rotate-45" />

                      <div className="relative px-5 py-4 border-b border-black/10 bg-black text-white">
                        <div className="font-mono text-[10px] tracking-widest text-white/50">SIGNED IN AS</div>
                        <div className="font-display tracking-widest text-sm mt-0.5 truncate">{user.name?.toUpperCase()}</div>
                        <div className="text-xs text-white/70 truncate mt-0.5">{user.email}</div>
                        {user.phone && <div className="text-xs text-white/70 truncate font-mono">+91 {user.phone}</div>}
                      </div>

                      {/* Wallet strip */}
                      <Link
                        to="/account"
                        onClick={() => setUserOpen(false)}
                        className="flex items-center justify-between px-5 py-3 border-b border-black/10 hover:bg-black/[0.03] transition-colors"
                        data-testid="menu-wallet"
                      >
                        <div>
                          <div className="font-mono text-[10px] tracking-widest text-black/50">ELMNTS WALLET</div>
                          <div className="font-display tracking-tighter text-2xl text-[#FF3B30] leading-none mt-1">{user.wallet_points || 0}</div>
                        </div>
                        <span className="font-mono text-[10px] text-black/40 tracking-widest">{(user.member_tier || "free").toUpperCase()} TIER</span>
                      </Link>

                      <nav className="py-2">
                        <MenuItem icon={<User size={14} />} label="MY ACCOUNT" onClick={() => { setUserOpen(false); navigate("/account"); }} testId="menu-account" />
                        <MenuItem icon={<Package size={14} />} label="MY ORDERS" onClick={() => { setUserOpen(false); navigate("/orders"); }} testId="menu-orders" />
                        <MenuItem icon={<Heart size={14} />} label="WISHLIST" onClick={() => { setUserOpen(false); navigate("/wishlist"); }} testId="menu-wishlist" />
                        <MenuItem icon={<MapPin size={14} />} label="TRACK ORDER" onClick={() => { setUserOpen(false); navigate("/orders"); }} testId="menu-track" />
                        {user.role === "admin" && (
                          <>
                            <div className="border-t border-black/10 my-2" />
                            <MenuItem icon={<ShieldCheck size={14} />} label="ADMIN PANEL" onClick={() => { setUserOpen(false); navigate("/admin"); }} testId="menu-admin" accent />
                          </>
                        )}
                      </nav>

                      <div className="border-t border-black/10">
                        <MenuItem icon={<LogOut size={14} />} label="SIGN OUT" onClick={handleLogout} testId="menu-logout" />
                      </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="font-display text-xs tracking-widest hidden sm:inline border border-black/30 px-4 py-2 hover-invert text-black" data-testid="login-nav-btn">
                LOGIN
              </Link>
            )}
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={doSearch} className="py-3 border-t border-black/10">
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, drops, collabs..."
              className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-2 font-display tracking-widest text-lg text-black placeholder:text-black/40"
              data-testid="search-input"
            />
          </form>
        )}
      </div>

      {/* MEGA MENU — slides under nav, full width, solid white */}
      {activeMega && MEGA[activeMega] && (
        <div
          className="hidden lg:block absolute top-full left-0 right-0 bg-white border-t border-b border-black/10 shadow-[0_20px_60px_-12px_rgba(0,0,0,0.18)] animate-fade-up"
          onMouseEnter={() => setActiveMega(activeMega)}
          onMouseLeave={() => setActiveMega(null)}
          data-testid={`mega-menu-${activeMega.toLowerCase()}`}
        >
          <div className="max-w-[1600px] mx-auto px-10 py-10 grid grid-cols-[1fr_1fr_1fr_320px] gap-10">
            {MEGA[activeMega].columns.map((col) => (
              <div key={col.title}>
                <div className="font-display tracking-widest text-xs text-[#FF3B30] border-b border-black/10 pb-3">{col.title}</div>
                <ul className="mt-4 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        onClick={() => setActiveMega(null)}
                        className="text-sm text-black/75 hover:text-[#FF3B30] transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* Feature card */}
            <Link
              to={MEGA[activeMega].feature.to}
              onClick={() => setActiveMega(null)}
              className="relative block aspect-[4/5] overflow-hidden group"
            >
              <img src={MEGA[activeMega].feature.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="font-mono text-[10px] tracking-widest text-[#FF3B30]">{MEGA[activeMega].feature.subtitle}</div>
                <div className="font-display text-3xl tracking-tighter mt-1">{MEGA[activeMega].feature.title}</div>
                <div className="mt-3 inline-flex items-center gap-1 font-display tracking-widest text-xs border-b border-white pb-1">{MEGA[activeMega].feature.cta} →</div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white">
          <div className="flex items-center justify-between p-6 border-b border-black/10">
            <span className="flex items-center gap-2 text-black">
              <img src="/logo-be-512.png" alt="" className="h-7 w-7 object-contain" />
              <span className="font-display text-2xl tracking-tighter leading-none">BYONDELMNTS</span>
            </span>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn" className="text-black"><X /></button>
          </div>
          <nav className="flex flex-col p-6 gap-5">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="font-display text-3xl tracking-tighter text-black"
              >
                {n.label}
              </Link>
            ))}
            <div className="border-t border-black/10 pt-5 mt-2 space-y-3 text-sm">
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="block text-black/70">Contact</Link>
              <Link to="/faqs" onClick={() => setMobileOpen(false)} className="block text-black/70">FAQs</Link>
              <Link to="/size-guide" onClick={() => setMobileOpen(false)} className="block text-black/70">Size Guide</Link>
            </div>
            {!user && (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="bg-black text-white text-center py-3 font-display tracking-widest mt-4">LOGIN / SIGN UP</Link>
            )}
          </nav>
        </div>
      )}
    </header>
    </>
  );
}

function MenuItem({ icon, label, onClick, testId, accent = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm font-display tracking-widest hover:bg-black hover:text-white transition-colors ${
        accent ? "text-[#FF3B30]" : "text-black"
      }`}
      role="menuitem"
    >
      <span className="text-current">{icon}</span>
      {label}
    </button>
  );
}
